import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './dashboard.css';
import './play.css';

type Invite = { matchId: string; ticket: string; friend?: string };
type Match = { state: string; expires: number; players: number; reason?: string };
function readInvite(): Invite | null {
  const params = new URLSearchParams(window.location.hash.slice(1));
  const matchId = params.get('match') || '';
  const ticket = params.get('ticket') || '';
  if (/^[a-f0-9-]{36}$/.test(matchId) && /^[a-f0-9]{64}$/.test(ticket)) {
    try {
      const saved = JSON.parse(sessionStorage.getItem('arena-invite') || 'null');
      if (saved?.matchId === matchId && saved.ticket === ticket) return saved;
    } catch { /* Fragment invite works even with storage disabled. */ }
    return { matchId, ticket };
  }
  return null;
}
function randomTicket() {
  return Array.from(crypto.getRandomValues(new Uint8Array(32)), b => b.toString(16).padStart(2, '0')).join('');
}
async function api(body: object) {
  const response = await fetch('/api/arena', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), cache: 'no-store' });
  const value = await response.json();
  if (!response.ok) throw new Error(value.error || 'Couldn’t reach the arena. Try again.');
  return value;
}

export default function Play({ theme }: { theme: string }) {
  const [invite, setInvite] = useState<Invite | null>(readInvite);
  const [match, setMatch] = useState<Match | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!invite) return;
    let cancelled = false;
    let running = false;
    const poll = async () => {
      if (running) return;
      running = true;
      try {
        const result = await api({ action: 'status', matchId: invite.matchId, ticket: invite.ticket });
        if (!cancelled) { setMatch(result); setError(''); }
      } catch (err) { if (!cancelled) setError((err as Error).message); }
      finally { running = false; }
    };
    poll();
    const timer = window.setInterval(() => { setNow(Date.now()); poll(); }, 4000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [invite]);

  const launch = async (event: React.FormEvent) => {
    event.preventDefault(); setBusy(true); setError('');
    try {
      // Keep the same id/tickets across retries if an HTTP response gets lost.
      let pending;
      try { pending = JSON.parse(sessionStorage.getItem('arena-pending') || 'null'); } catch { /* optional storage */ }
      if (!pending) pending = { matchId: crypto.randomUUID(), tickets: [randomTicket(), randomTicket()] };
      try { sessionStorage.setItem('arena-pending', JSON.stringify(pending)); } catch { /* optional */ }
      const value = await api({ action: 'launch', code, ...pending });
      const next = { matchId: value.matchId, ticket: pending.tickets[0], friend: pending.tickets[1] };
      try { sessionStorage.setItem('arena-invite', JSON.stringify(next)); sessionStorage.removeItem('arena-pending'); } catch { /* optional */ }
      window.history.replaceState(null, '', '/play#match=' + next.matchId + '&ticket=' + next.ticket);
      setCode(''); setMatch(value); setInvite(next);
    } catch (err) { setError((err as Error).message); }
    finally { setBusy(false); }
  };
  const stop = async () => {
    if (!invite) return;
    setBusy(true);
    try { setMatch(await api({ action: 'stop', matchId: invite.matchId, ticket: invite.ticket })); }
    catch (err) { setError((err as Error).message); }
    finally { setBusy(false); }
  };
  const friendUrl = invite?.friend ? window.location.origin + '/play#match=' + invite.matchId + '&ticket=' + invite.friend : '';
  const seconds = match ? Math.max(0, match.expires - Math.floor(now / 1000)) : 0;
  const ready = match?.state === 'ready';
  const ended = match?.state === 'ended';
  return <div className="dashboard arena-page" id={theme}><div className="desk-shell">
    <header className="site-heading"><Link to="/" className="site-name">ｋｉｒａｎ . wtf</Link><span className="site-caption">settle it on redline.</span><Link to="/" className="arena-back">← back to the tabs</Link></header>
    <main className="arena-lobby">
      <p className="eyebrow">RIFLE ARENA / AIM_REDLINE / FIRST TO 10</p>
      <h1>1v1 me.</h1>
      <p className="arena-intro">One rifle. Two players. No excuses.</p>
      {!invite ? <form className="arena-card" onSubmit={launch}>
        <label htmlFor="launch-code">the launch code</label>
        <input id="launch-code" type="password" value={code} onChange={event => setCode(event.target.value)} autoComplete="off" required maxLength={128} placeholder="ask kiran" disabled={busy} />
        <button type="submit" disabled={busy}>{busy ? 'starting your arena…' : 'launch a duel →'}</button>
        <p>Start a match, then send a friend their invite. Runs in your browser.</p>
      </form> : <section className="arena-card" aria-label="Your match">
        <div className="arena-status" role="status"><span className="status-dot" />{ended ? 'match over' : ready ? 'arena ready' : match?.state === 'ending' ? 'shutting down…' : 'starting the arena…'}</div>
        {!ended && <p>{match ? `${match.players}/2 connected · ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')} remaining` : 'Checking your invite…'}</p>}
        {friendUrl && !ended && <div className="arena-invite"><label htmlFor="friend-link">send this to your opponent</label><div><input id="friend-link" readOnly value={friendUrl} onFocus={event => event.target.select()} /><button type="button" onClick={async () => {
          try { await navigator.clipboard.writeText(friendUrl); setCopied(true); } catch { setError('Select the invite link above and copy it.'); }
        }}>{copied ? 'copied ✓' : 'copy link'}</button></div></div>}
        {ready && invite && <a className="arena-enter" href={'/arena/index.html#match=' + invite.matchId + '&ticket=' + invite.ticket}>enter the arena →</a>}
        {!ready && !ended && <p>Usually ready in under a minute. You can share the invite while it starts.</p>}
        {invite.friend && !ended && <button className="arena-stop" onClick={stop} disabled={busy}>end match</button>}
        {ended && <><p>{match?.reason || 'Good game.'}</p><button onClick={() => { window.history.replaceState(null, '', '/play'); setInvite(null); setMatch(null); setError(''); }}>another round →</button></>}
      </section>}
      {error && <p className="arena-error" role="alert">{error}</p>}
      <div className="arena-rules"><p>WASD move · mouse aim · click fire · R reload<br />Space jump · C crouch · Shift walk · Esc release mouse</p><p>One match at a time. Servers shut down after the match, inactivity, or 15 minutes. Keep hardware acceleration enabled.</p></div>
    </main>
    <footer className="desk-footer"><span>ｋｉｒａｎ . wtf</span><span>see you on the other side.</span><Link to="/">home ↗</Link></footer>
  </div></div>;
}
